import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraProgramSidebar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle, ArrowLeft, BarChart3, Building2, Calendar, CalendarClock, ClipboardCheck, Contact, DollarSign, FileSpreadsheet, FileText, Handshake, Layers, LayoutDashboard, ListChecks, ListTodo, MapPin, MessagesSquare, Scan, Settings2, Shield, ShieldCheck, Users, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useView } from '../lib/access';
import { financialSectionVisible, programRef } from '../lib/access/types';
import { renderRouterLink } from '../lib/renderRouterLink';
import { cn } from '../lib/utils';
import { POTENTIAL_ISSUES_COPY, WAIVERS_TAILORING_COPY } from '../pages/Delivery/_shared/constants/sectionCopy';
import { useActivationGate } from '../pages/Delivery/Programs/activationGateHooks';
import { useProgram } from '../pages/Delivery/Programs/hooks';
import { SidebarUserMenu } from './SidebarUserMenu';
const FAVORITES_KEY = 'specra-program-nav-favorites';
function loadFavorites() {
    try {
        const raw = window.localStorage.getItem(FAVORITES_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((v)=>typeof v === 'string') : [];
    } catch  {
        return [];
    }
}
const iconClass = 'w-4 h-4 flex-shrink-0';
// Group taxonomy from the redesign mockup (nav model lines 3209–3239).
// Activation Gate is a real route the mockup lacks — it stays in the top
// ungrouped section next to Home, matching its current placement.
const PRIMARY_DEFS = [
    {
        id: 'home',
        label: 'Home',
        path: '',
        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
            className: iconClass
        }),
        testId: Testids.programNav.home,
        pinnable: false
    },
    {
        id: 'activation-gate',
        label: 'Activation Gate',
        path: 'activation-gate',
        icon: /*#__PURE__*/ _jsx(ShieldCheck, {
            className: iconClass
        }),
        testId: Testids.programNav.activationGate,
        pinnable: false
    },
    {
        // Unlike Activation Gate, Closeout is always visible — every program
        // can be closed out, regardless of how it originated.
        id: 'closeout',
        label: 'Closeout',
        path: 'closeout',
        icon: /*#__PURE__*/ _jsx(ListChecks, {
            className: iconClass
        }),
        testId: Testids.programNav.closeout,
        pinnable: false
    },
    {
        id: 'workroom',
        label: 'Workroom',
        path: 'workroom',
        icon: /*#__PURE__*/ _jsx(MessagesSquare, {
            className: iconClass
        }),
        testId: Testids.programNav.workroom,
        pinnable: false
    }
];
const GROUP_DEFS = [
    {
        id: 'people-org',
        label: 'People & Org',
        items: [
            {
                id: 'roles',
                label: 'Roles',
                path: 'roles',
                icon: /*#__PURE__*/ _jsx(Users, {
                    className: iconClass
                }),
                testId: Testids.programNav.roles
            },
            {
                id: 'companies',
                label: 'Companies',
                path: 'companies',
                icon: /*#__PURE__*/ _jsx(Building2, {
                    className: iconClass
                }),
                testId: Testids.programNav.companies
            },
            {
                id: 'contacts',
                label: 'Contacts',
                path: 'contacts',
                icon: /*#__PURE__*/ _jsx(Contact, {
                    className: iconClass
                }),
                testId: Testids.programNav.contacts
            }
        ]
    },
    {
        id: 'contract-scope',
        label: 'Contract & Scope',
        items: [
            {
                id: 'clins',
                label: 'CLINs',
                path: 'clins',
                icon: /*#__PURE__*/ _jsx(FileSpreadsheet, {
                    className: iconClass
                }),
                testId: Testids.programNav.clins
            },
            {
                id: 'workstreams',
                label: 'Teams',
                path: 'workstreams',
                icon: /*#__PURE__*/ _jsx(Layers, {
                    className: iconClass
                }),
                testId: Testids.programNav.workstreams
            },
            {
                id: 'capabilities',
                label: 'Capabilities',
                path: 'capabilities',
                icon: /*#__PURE__*/ _jsx(Zap, {
                    className: iconClass
                }),
                testId: Testids.programNav.capabilities
            },
            {
                id: 'deliverables',
                label: 'Deliverables',
                path: 'deliverables',
                icon: /*#__PURE__*/ _jsx(ListTodo, {
                    className: iconClass
                }),
                testId: Testids.cmNav.deliverables
            },
            {
                id: 'weekly-recap',
                label: 'Weekly Recap',
                path: 'weekly-recap',
                icon: /*#__PURE__*/ _jsx(CalendarClock, {
                    className: iconClass
                }),
                testId: Testids.programNav.weeklyRecap
            },
            {
                id: 'optional-tasks',
                label: 'Optional Tasks',
                path: 'optional-tasks',
                icon: /*#__PURE__*/ _jsx(ListChecks, {
                    className: iconClass
                }),
                testId: Testids.programNav.optionalTasks
            },
            {
                id: 'places',
                label: 'Places',
                path: 'places',
                icon: /*#__PURE__*/ _jsx(MapPin, {
                    className: iconClass
                }),
                testId: Testids.programNav.places
            },
            {
                id: 'periods',
                label: 'Periods',
                path: 'periods',
                icon: /*#__PURE__*/ _jsx(Calendar, {
                    className: iconClass
                }),
                testId: Testids.programNav.periods
            },
            {
                id: 'settings',
                label: 'Settings',
                path: 'settings',
                icon: /*#__PURE__*/ _jsx(Settings2, {
                    className: iconClass
                }),
                testId: Testids.cmNav.settings
            }
        ]
    },
    {
        id: 'compliance',
        label: 'Compliance',
        items: [
            {
                id: 'audits',
                label: 'Audits',
                path: 'audits',
                icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                    className: iconClass
                }),
                testId: Testids.programNav.audits
            },
            {
                id: 'deviations',
                label: WAIVERS_TAILORING_COPY.label,
                path: 'waivers-tailoring',
                icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                    className: iconClass
                }),
                testId: Testids.programNav.deviations
            },
            {
                id: 'quality',
                label: 'Quality',
                path: 'quality',
                icon: /*#__PURE__*/ _jsx(BarChart3, {
                    className: iconClass
                }),
                testId: Testids.programNav.quality
            },
            {
                id: 'risk',
                label: POTENTIAL_ISSUES_COPY.label,
                path: 'potential-issues',
                icon: /*#__PURE__*/ _jsx(Shield, {
                    className: iconClass
                }),
                testId: Testids.programNav.risk
            },
            {
                id: 'ops-audit',
                label: 'History',
                path: 'ops-audit',
                icon: /*#__PURE__*/ _jsx(Scan, {
                    className: iconClass
                }),
                testId: Testids.programNav.opsAudit
            }
        ]
    },
    {
        id: 'growth',
        label: 'Growth',
        items: [
            {
                id: 'touchpoints',
                label: 'Touchpoints',
                path: 'touchpoints',
                icon: /*#__PURE__*/ _jsx(Handshake, {
                    className: iconClass
                }),
                testId: Testids.programNav.touchpoints
            }
        ]
    },
    {
        id: 'financial',
        label: 'Financial',
        items: [
            {
                id: 'financial',
                label: 'Financial',
                path: 'financial',
                icon: /*#__PURE__*/ _jsx(DollarSign, {
                    className: iconClass
                }),
                testId: Testids.programNav.financial
            }
        ]
    },
    {
        id: 'documents',
        label: 'Documents',
        items: [
            {
                id: 'docs',
                label: 'Docs',
                path: 'docs',
                icon: /*#__PURE__*/ _jsx(FileText, {
                    className: iconClass
                }),
                testId: Testids.programNav.docs
            }
        ]
    }
];
export function ProgramSidebar({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const location = useLocation();
    const { user } = useAuth();
    const [desktopCollapsed, setCollapsed] = useState(false);
    const isNarrow = useMediaQuery('(max-width: 767px)');
    const collapsed = isNarrow || desktopCollapsed;
    const [favorites, setFavorites] = useState(loadFavorites);
    const [groupState, setGroupState] = useState({});
    // ⌘\ / Ctrl+\ toggles the rail (LAYOUT-SPEC §7).
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === '\\' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setCollapsed((value)=>!value);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, []);
    // Favorites persist locally (plan decision: localStorage, no backend).
    useEffect(()=>{
        try {
            window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
        } catch  {
        // Storage unavailable (private mode) — favorites stay session-local.
        }
    }, [
        favorites
    ]);
    const togglePin = useCallback((id)=>{
        setFavorites((current)=>current.includes(id) ? current.filter((v)=>v !== id) : [
                ...current,
                id
            ]);
    }, []);
    // Permission-aware nav: hide the Financial group for viewers who can see
    // neither figure the Financial Health tab exposes — same predicate
    // KpiSpine's Financial tile uses (financialSectionVisible), so the nav and
    // the tile never disagree.
    const showFinancial = financialSectionVisible(useView(programRef(programId)).fieldMasked);
    // The activation gate only exists on converted (CommitImport) programs —
    // presence of the gate IS the origin signal; hidden until resolved so the
    // item never flashes (query cached, shared with ActivationGatePage).
    const { data: activationGate } = useActivationGate(programId);
    const showActivationGate = activationGate != null;
    const { data: programData } = useProgram(programId);
    const programName = programData?.program?.programNameShort || programData?.program?.programNameFull || 'Program';
    const contractNumber = programData?.program?.contractNumber || undefined;
    const isActive = useCallback((def)=>{
        if (def.path === '') return location.pathname === base;
        const href = `${base}/${def.path}`;
        return location.pathname === href || location.pathname.startsWith(`${href}/`);
    }, [
        base,
        location.pathname
    ]);
    const toItem = useCallback((def)=>({
            id: def.id,
            label: def.label,
            icon: def.icon,
            href: def.path === '' ? base : `${base}/${def.path}`,
            active: isActive(def),
            testId: def.testId,
            pinnable: def.pinnable !== false,
            pinned: favorites.includes(def.id),
            onTogglePin: ()=>togglePin(def.id),
            pinTestId: Testids.programNav.pin
        }), [
        base,
        favorites,
        isActive,
        togglePin
    ]);
    const groups = useMemo(()=>{
        const visibleGroups = GROUP_DEFS.filter((group)=>group.id !== 'financial' || showFinancial);
        const defsById = new Map(visibleGroups.flatMap((g)=>g.items).map((d)=>[
                d.id,
                d
            ]));
        // Pinned items duplicate into the top ungrouped section (mockup 3218–3220);
        // the duplicate carries its own testid so journeys can target it without
        // colliding with the original item's selector.
        const pinnedItems = favorites.map((id)=>defsById.get(id)).filter((def)=>def != null).map((def)=>({
                ...toItem(def),
                id: `pinned-${def.id}`,
                testId: Testids.programNav.pinnedItem
            }));
        const primary = PRIMARY_DEFS.filter((def)=>def.id !== 'activation-gate' || showActivationGate).map(toItem);
        return [
            {
                id: 'primary',
                items: [
                    ...primary,
                    ...pinnedItems
                ]
            },
            ...visibleGroups.map((group)=>({
                    id: group.id,
                    label: group.label,
                    items: group.items.map(toItem)
                }))
        ];
    }, [
        favorites,
        showActivationGate,
        showFinancial,
        toItem
    ]);
    return /*#__PURE__*/ _jsx(SpecraProgramSidebar, {
        backAction: /*#__PURE__*/ _jsxs(Link, {
            className: cn('flex items-center gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground', collapsed && 'justify-center'),
            "data-testid": Testids.programNav.back,
            title: "All programs",
            to: "/delivery/programs",
            children: [
                /*#__PURE__*/ _jsx(ArrowLeft, {
                    "aria-hidden": "true",
                    className: "h-3.5 w-3.5 flex-shrink-0"
                }),
                !collapsed && /*#__PURE__*/ _jsx("span", {
                    className: "truncate",
                    children: "All programs"
                })
            ]
        }),
        className: "h-full max-md:max-h-none max-md:w-[var(--component-sidebar-collapsed-width)]",
        collapsed: collapsed,
        collapseToggleTestId: Testids.programNav.collapse,
        groups: groups,
        footer: user ? /*#__PURE__*/ _jsx(SidebarUserMenu, {
            collapsed: collapsed,
            user: user
        }) : undefined,
        getGroupProps: (group)=>group.label ? {
                collapsible: true,
                containsActive: group.items.some((item)=>item.active),
                labelTestId: Testids.programNav.groupLabel,
                onToggle: ()=>setGroupState((state)=>({
                            ...state,
                            [group.id]: !(state[group.id] ?? true)
                        })),
                // Groups default open (deviation from the mockup's closed-unless-
                // active default) so deep nav clicks — and every existing
                // program_* journey — keep working without a label click first.
                open: groupState[group.id] ?? true
            } : {},
        navigationLabel: "Program navigation",
        navigationTestId: Testids.programNav.sidebar,
        onCollapseToggle: isNarrow ? undefined : ()=>setCollapsed((value)=>!value),
        programId: contractNumber,
        programName: programName,
        renderLink: renderRouterLink
    });
}
